'use strict';
/**
 * Base para entidades
 * Propriedades na raiz iniciados com _ são ignorados da grvação no banco
 * O firebase não aceita uma instância de classe como parâmetro, é necessário transformar em um objeto comum,
 *    toFirestore faz isso para você
 * Não há tratamento especial para o campo id
 * Passar propriedades inexixstentes na Entidade para o contrutor ou não tem efeito.
 * Definir uma propriedade nova diretamente funciona, mas não é recomendado
 */

exports.default = exports;
const moment = require('moment');
const _ = require('lodash');
const NumberUtils = require('../NumberUtils');
const StringUtils = require('../StringUtils');

const Types = {
  STRING: 'STRING',
  ID_FK: 'ID_FK',
  NUMBER: 'NUMBER',
  CURRENCY: 'CURRENCY', // Arredonda com 2 casas decimais
  BOOLEAN: 'BOOLEAN',
  DATE: 'DATE',
  DATE_ISO: 'DATE_ISO',
  STRUCT_FK: 'STRUCT_FK',
  ARRAY_FK: 'ARRAY_FK',
  ARRAY_ID_FK: 'ARRAY_ID_FK',
  ARRAY_STRING: 'ARRAY_STRING',
  SEARCH: 'SEARCH',
  ORIGINAL: 'ORIGINAL', // Não mexer nesse campo
};
exports.Types = Types;

/**
 @typedef {keyof Types} BaseEntity.Types
 */

/**
 * @typedef {Object} ExtraInfo
 * @property {string} namePg
 * @property {Boolean} [ignoreOnPG=undefined]
 * @property {Boolean} [ignoreOnGQL=undefined]
 * @property {string[]} [searchProps=undefined]
 * @property {*} [defaultValue=undefined]
 * @property {*} [useConnect=undefined]
 * @property {*} [entity=undefined]
 */

/**
 * @param {Types} type
 * @param {string|null} info
 * @param {ExtraInfo}
 * @returns {{ignoreOnPG: *, defaultValue: *, type: null, namePg: *, info: null, entity: *, useConnect: *, ignoreOnGQL: *}}
 */

function sf(type=null, info=null, {namePg, ignoreOnPG, searchProps, defaultValue, entity, useConnect, ignoreOnGQL}={}) {
  return {type, info, namePg, ignoreOnPG, searchProps, defaultValue, entity, useConnect, ignoreOnGQL};
}

exports.sf = sf;


function internalToFirestore(obj, count=7) {
  if (typeof obj?.toDate==='function') {
    return obj.toDate();
  }
  if (obj instanceof Date) {
    return obj;
  }
  let lazy = null;
  if (obj instanceof BaseEntity) {
    lazy = {...obj};
  }
  else {
    lazy = _.clone(obj);
  }
  for (const key of Object.keys(lazy || {})) {
    if (key[0] === '_') {
      delete lazy[key];
    }
    else if (lazy[key] && typeof lazy[key]==='object') {
      if (typeof lazy[key].toFirestore==='function') {
        lazy[key] = lazy[key].toFirestore();
      }
      else {
        lazy[key] = internalToFirestore(lazy[key], count-1);
      }
    }
  }
  return lazy;
}

function internalToFirestoreClear(obj, count=7) {
  if (typeof obj?.toDate==='function') {
    return obj.toDate();
  }
  if (obj instanceof Date) {
    return obj;
  }
  let lazy = null;
  if (obj instanceof BaseEntity) {
    lazy = {...obj};
  }
  else {
    lazy = _.clone(obj);
  }
  for (const key of Object.keys(lazy || {})) {
    if (key[0] === '_') {
      delete lazy[key];
    }
    else if (lazy[key] && typeof lazy[key]==='object') {
      if (typeof lazy[key].toFirestoreClear==='function') {
        lazy[key] = lazy[key].toFirestoreClear();
      }
      else {
        const tmp = internalToFirestoreClear(lazy[key], count-1);
        if (_.isEmpty(tmp) && !(tmp instanceof Date) && typeof tmp==='object') {
          delete lazy[key];
        }
        else {
          lazy[key] = tmp;
        }
      }
    }
  }
  return lazy;
}

function convertArrayToObject(obj, sufix='Obj', key='id', count=7) {
  if (typeof obj?.toDate==='function') {
    return obj.toDate();
  }
  if (obj instanceof Date) {
    return obj;
  }
  let lazy = null;
  if (obj instanceof BaseEntity) {
    lazy = {...obj};
  }
  else {
    lazy = _.clone(obj);
  }
  for (const key of Object.keys(lazy || {})) {
    let newKey = key;
    if (key[0] === '_') {
      delete lazy[key];
    }
    else if (lazy[key] && typeof lazy[key]==='object') {
      if (Array.isArray(lazy[key])) {
        newKey = key+sufix;
      }
      else {
        const tmp = convertArrayToObject(lazy[key], sufix, key, count-1);
        if (_.isEmpty(tmp) && !(tmp instanceof Date) && typeof tmp==='object') {
          delete lazy[newKey];
        }
        else {
          lazy[newKey] = tmp;
        }
      }
      if (Array.isArray(lazy[key])) {
        delete lazy[key];
      }
    }
  }
  return lazy;
}


function internalToPrisma(rootObj, rootConfig, obj, config, count=7) {

  if (typeof config?.beforeSave === 'function') {
    config?.beforeSave(obj, rootObj);
  }
  if (typeof obj?.toDate==='function') {
    return obj.toDate();
  }
  if (obj instanceof Date) {
    return obj;
  }
  let lazy = null;
  if (obj instanceof BaseEntity) {
    lazy = {...obj};
  }
  else {
    lazy = _.clone(obj);
  }
  for (const key of Object.keys(lazy || {})) {
    const configKey = config?.fields?.[key];
    if (config && !configKey) { // remover propriedades não configuradas
      delete lazy[key];
      continue;
    }
    let configInfo = null;
    if (config?.fields?.[key]?.info) {
      configInfo = rootConfig[config.fields[key].info] || null;
    }
    if (key[0] === '_' || configKey?.ignoreOnPG) {
      delete lazy[key];
    }
    else if (lazy[key] && typeof lazy[key]==='object') {
      if (typeof lazy[key].prepareToPrisma==='function') {
        lazy[key] = lazy[key].prepareToPrisma();
      }
      else if (configKey?.useConnect) {
        lazy[configKey?.useConnect.field] = {
          connect: {
            [configKey?.useConnect.child]: internalToPrisma(rootObj, rootConfig, lazy[key], configInfo, count-1)
          }
        };
        delete lazy[key];
      }
      else if (Array.isArray(lazy[key])) {
        if (configKey?.type===Types.ARRAY_ID_FK || configKey?.type===Types.ARRAY_STRING) {
          for (const keyA in lazy[key]) {
            lazy[key][keyA] = String(lazy[key][keyA]);
          }
        }
        else if (configKey?.type===Types.ARRAY_FK) {
          for (const keyA in lazy[key]) {
            lazy[key][keyA] = internalToPrisma(rootObj, rootConfig, lazy[key][keyA], configInfo, count-1);
          }
        }
        // else {
        //   for (const keyA in lazy[key]) {
        //     lazy[key][keyA] = internalToPrisma(rootObj, rootConfig, lazy[key][keyA], configInfo, count-1);
        //   }
        // }
      }
      else {
        lazy[key] = internalToPrisma(rootObj, rootConfig, lazy[key], configInfo, count-1);
      }
    }
    else {
      if (configKey?.useConnect) {
        lazy[configKey?.useConnect.field] = {
          connect: {
            [configKey?.useConnect.child]: String(lazy[key])
          }
        };
        delete lazy[key];
      }
    }
  }
  return lazy;
}

function internalFixTypes(rootObj, rootConfig, obj, config, count=7) {
  if (typeof obj?.toDate==='function') {
    return obj.toDate();
  }

  if (!config) {
    return obj;
  }

  for (const key in config?.fields || {}) {
    const field = config.fields[key];
    let vtmp = obj[key] ?? field?.defaultValue ?? null;
    let configInfo = null;
    if (field?.info) {
      configInfo = rootConfig[field.info] || null;
    }
    switch (field.type) {
      case Types.BOOLEAN:
        vtmp = Boolean(vtmp);
        break;
      case Types.NUMBER:
        vtmp = Number(vtmp || 0);
        break;
      case Types.STRING:
        if(vtmp!==null) {
          vtmp = String(vtmp);
        }
        break;
      case Types.DATE:
        break;
      case Types.ORIGINAL:
        break;
      case Types.CURRENCY:
        vtmp = NumberUtils.currency(vtmp || 0);
        break;
      case Types.ID_FK:
        if(vtmp!==null) {
          vtmp = String(vtmp);
        }
        break;
      case Types.DATE_ISO:
        if(vtmp!==null) {
          vtmp = String(vtmp);
        }
        break;
      case Types.STRUCT_FK:
        if (vtmp) {
          vtmp = internalFixTypes(rootObj, rootConfig, obj[key], configInfo, count-1);
        }
        break;
      case Types.ARRAY_FK:
        vtmp = [];
        for (const keyA in obj[key] || []) {
          vtmp.push(internalFixTypes(rootObj, rootConfig, obj[key][keyA], configInfo, count-1));
        }
        break;
      case Types.ARRAY_ID_FK:
        vtmp = [];
        for (const keyA in obj[key] || []) {
          vtmp.push(String(obj[key][keyA]));
        }
        break;
      case Types.ARRAY_STRING:
        vtmp = [];
        for (const keyA in obj[key] || []) {
          vtmp.push(String(obj[key][keyA]));
        }
        break;
      case Types.SEARCH:
        vtmp = StringUtils.getSearch(field.searchProps?.map(a=>obj[a]) || []);
        break;
      default:
        console.error('Error, type: ' + field.type + ' não reconhecido: '+key, field, obj);
      // continue;
    }
    obj[key] = vtmp;
    if (obj[key] === 'undefined') {
      delete obj[key];
    }
  }
  return obj;
}

class BaseEntity {
  static _defaults = {};
  static _configs = {};
  constructor(obj) {
    // setFcm não pode ser chamado aqui pois as propriedades ainda não foram definidas
    // É necessário chamar o this.setFcm(obj) no construtor da classe extendida
  }
  setFcm(obj) {
    if(typeof obj?.updatedAt?.toDate ==='function') {
      obj.updatedAt = obj.updatedAt.toDate();
    }
    if(typeof obj?.deletedAt?.toDate ==='function') {
      obj.deletedAt = obj.deletedAt.toDate();
    }
    if(typeof obj?.createdAt?.toDate ==='function') {
      obj.createdAt = obj.createdAt.toDate();
    }

    if(obj?.updatedAt && typeof obj.updatedAt === 'string') {
      obj.updatedAt = moment(obj.updatedAt)?.toDate();
    }
    if(obj?.deletedAt && typeof obj.deletedAt === 'string') {
      obj.deletedAt = moment(obj.deletedAt)?.toDate();
    }
    if(obj?.createdAt && typeof obj.createdAt === 'string') {
      obj.createdAt = moment(obj.createdAt)?.toDate();
    }

    if(!obj) {
      return;
    }
    for(const key of Object.keys(this)) {
      if(typeof obj[key]!=='undefined') {
        this[key] = obj[key];
      }
    }
    return this;
  }

  /**
   * @returns {{}}
   */
  getMutation() {
    return BaseEntity.getMutation(this);
  }

  /**
   *
   * @param {object} data
   * @returns {{}}
   */
  static getMutation(data) {
    const newData = {};
    for (const key of Object.keys(data || {})) {
      if(key[0]==='_') {
        continue;
      }
      if(data[key] !== undefined) {
        newData[key] = {
          set: data[key]
        };
      }
    }
    return newData;
  }

  /**
   *
   * @param {string='id'} key
   * @returns {{update: {}, create: {}, [key]: string}}
   */
  getFullMutation(key='id') {
    return BaseEntity.getFullMutation(this, key, this.constructor._configs?.main);
  }

  /**
   *
   * @param {object} data
   * @param {string='id'} keyWhere
   * @param config
   * @returns {{update: {}, create: {}, where: {}}}
   */
  static getFullMutation(data, keyWhere='id', config = null) {
    const update = {};
    const create = _.cloneDeep(data);
    for (const key of Object.keys(create || {})) {
      if(key[0]==='_') {
        delete create[key];
        continue;
      }
      const fieldConfig = config?.fields?.[key];

      if (fieldConfig?.useConnect) {
        update[fieldConfig.useConnect.field] = {
          connect: {
            [fieldConfig.useConnect.child]: create[key]
          }
        };
        create[fieldConfig.useConnect.field] = {
          connect: {
            [fieldConfig.useConnect.child]: create[key]
          }
        };
        delete create[key];
        continue;
      }

      if (typeof create[key]?.toDate ==='function') {
        create[key] = create[key].toDate();
      }

      if(create[key] !== undefined) {
        update[key] = {
          set: create[key]
        };
      }
      if(Array.isArray(create[key])) {
        create[key] = {set: create[key]};
      }
    }


    // if (keyWhere !== 'id') {
    //   return {
    //     update: update,
    //     create: create,
    //     where: {
    //       [keyWhere]: {
    //         equals: create[keyWhere]
    //       }
    //     },
    //   };
    // }

    return {
      update: update,
      create: create,
      where: {
        [keyWhere]: create[keyWhere]
      },
    };
  }

  /*
   * @deprecated em favor de toFirestore
   * @returns {BaseEntity}
   */
  // getFirestoreObject() {
  //   const lazy = {...this};
  //   for (const key of Object.keys(lazy || {})) {
  //     if (key[0] === '_') {
  //       delete lazy[key];
  //     }
  //   }
  //   return lazy;
  // }

  /*
   * @deprecated em favor de fromFirestore
   * @param obj
   * @returns {BaseEntity}
   */
  // static createByFirestoreObject(obj) {
  //   const result = new this(obj.data());
  //   result.id = obj.id;
  //   result.makeSearch();
  //   return result;
  // }

  static toFirestore(obj) {
    if (typeof obj.fixTypes==='function') {
      obj.fixTypes();
    }
    if (typeof obj.toFirestore==='function') {
      return obj.toFirestore();
    }
    return internalToFirestore(this);
  }

  // nomeclatura usada pelo firestore no .withConverter  ex: doc(db, "cities", "LA").withConverter(PaymentEntity);
  toFirestore() {
    return internalToFirestore(this);
  }

  toFirestoreClear() {
    return internalToFirestoreClear(this);
  }

  // nomeclatura usada pelo firestore no .withConverter  ex: doc(db, "cities", "LA").withConverter(PaymentEntity);
  static fromFirestore(obj) {
    const result = new this(obj.data());
    result.id = obj.id;
    result.makeSearch();
    return result;
  }

  static toMakeHistory(obj) {
    const tmp = _.cloneDeep(obj);
    delete tmp.meta;
    return convertArrayToObject(tmp);
  }

  fixTypes() {
    internalFixTypes(this, this.constructor._configs, this, this.constructor._configs?.main);
    return this;
  }

  prepareToPrisma() {
    this.fixTypes();
    return internalToPrisma(this, this.constructor._configs, this, this.constructor._configs?.main);
    //
    // const obj = _.cloneDeep(this);
    //
    // if (typeof this.constructor._configs?.main?.beforeSave === 'function') {
    //   this.constructor._configs?.main?.beforeSave(this);
    // }
    // for (const key in obj) {
    //   if (key[0]==='_') {
    //     delete obj[key];
    //   }
    //   if (this.constructor._configs?.main?.fields[key]?.ignoreOnPG) {
    //     delete obj[key];
    //   }
    // }
    //
    // return obj;
  }

  prismaUpsert() {
    const tmp = this.prepareToPrisma();
    return {
      where: {
        id: this.id,
      },
      update: tmp,
      create: tmp
    };
  }


  makeSearch() {
    // Exemplos

    // this._search = StringUtils.deburr(this.name);

    // this._search = StringUtils.deburr([
    //   this.fetched.receiver.name,
    //   this.fetched.hospital.name,
    //   this.fetched.payer.name,
    //   this.fetched.payer.CNPJ
    // ].filter(a=>!!a).join(' '));
  }


  /**
   * Populate the object with a sampler of entity
   */
  example() {
    return this;
  }


  test() {
    console.log(this.constructor._defaults);
  }
}


BaseEntity._configs = {
  meta: {
    fields: {
      createdAt: sf(Types.DATE),
      updatedAt: sf(Types.DATE),
      deletedAt: sf(Types.DATE),

      createdBy: sf(Types.ID_FK),
      deletedBy: sf(Types.ID_FK),
      updatedBy: sf(Types.ID_FK),

      createdEnv: sf(Types.STRING),
      updatedEnv: sf(Types.STRING),
      deletedEnv: sf(Types.STRING),
      updatedByAdmin: sf(Types.BOOLEAN),
    }
  }
};

exports.BaseEntity = BaseEntity;
exports.internalToFirestore = internalToFirestore;
exports.internalToFirestoreClear = internalToFirestoreClear;
exports.convertArrayToObject = convertArrayToObject;

// https://www.typescriptlang.org/docs/handbook/2/mapped-types.html
// type OnlyBoolsAndHorses = {
//   [key: string]: boolean | Horse;
// };

//
// type OptionsFlags<Type> = {
//   [Property in keyof Type]: boolean;
// };
// type Concrete<Type> = {
//   [Property in keyof Type]-?: Type[Property];
// };

//
// type Getters<Type> = {
//   [Property in keyof Type as `get${Capitalize<string & Property>}`]: () => Type[Property]
// };
//
// interface Person {
//   name: string;
//   age: number;
//   location: string;
// }
//
// type LazyPerson = Getters<Person>;
